/**
 * Wires the new typed apiClient (`@/services/api/client`) with the resolvers and
 * error handlers it needs at runtime.
 *
 * Consolidates four cross-cutting auth-error paths that legacy splits across
 * two places:
 *   • `@/domain/api.js` `doLogout` (401, trial-expired) — saga-level, called manually
 *   • `@/lib/apiHelpers.js` `getResponseInterceptors` (426, access-denied) — global axios
 *
 * The new client handles all four in one response interceptor via the callbacks
 * below. Sagas migrated to bankFeedApi.* drop their `doLogout` call — this is
 * its replacement. Legacy sagas calling Api.X keep `doLogout` until they too
 * migrate (then the legacy locations can be deleted in a separate cleanup PR).
 */
import type { AxiosError } from 'axios';

import { CONFIGURABLE_API_ENDPOINT } from '@/lib/apiHelpers';
import { configureApiClient } from '@/services/api/setup';
import { eventsApi, bankFeedApi } from '@/services/api/instances';
import { getAuthToken } from '@/services/api/auth';
import { getCompanyId } from '@/services/api/companyId';
import { signOutAction, expiredAction } from '@/domain/env';

// Structural type — domain/store.js is plain JS, no AppStore type to import.
// Once the store gains real types, replace with `import type { AppStore }`.
interface StoreLike {
  dispatch: (action: { type: string; payload?: unknown }) => void;
  getState: () => { env: { get: (key: 'locale') => string | undefined } };
}

// 426 Upgrade Required: backend signals the loaded JS bundle is stale. We reload
// up to N times — the service worker often re-serves the same stale bundle until
// its cache cycles — then force re-login so the next session picks up fresh JS.
const RELOAD_TRIES_KEY = 'dokka-app-reload-tries';
const MAX_RELOAD_TRIES = 4;

export default function apiClientInitializer(store: StoreLike): void {
  const config = {
    baseURL: `${CONFIGURABLE_API_ENDPOINT || ''}/v2/3/`,
    resolveAuthHeaders: async () => {
      // Read before the await so every header in the returned set describes the
      // same moment — the one the request was issued in.
      const companyId = getCompanyId();
      const token = await getAuthToken();
      const locale = store.getState().env.get('locale');
      return {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(companyId && { CompanyId: companyId }),
        ...(locale && { 'Accept-Language': locale }),
      };
    },
    onAuthFailure: () => store.dispatch({ type: signOutAction.type }),
    onTrialExpired: () => store.dispatch({ type: expiredAction.type }),
    onAccessDenied: (err: AxiosError) => {
      // Backend may include a `type` discriminator on the response body
      // (e.g. 'BLOCKED', 'SUSPENDED'). UI uses it to render specific
      // access-denied screens distinct from a plain sign-out.
      const data = err.response?.data as { type?: string } | undefined;
      const accessDeniedReasonType = data?.type;
      store.dispatch({
        type: signOutAction.type,
        payload: {
          reason: process.env.REACT_APP_ACCESS_DENIED_CODE,
          ...(accessDeniedReasonType && { accessDeniedReasonType }),
        },
      });
    },
    onUpgradeRequired: () => {
      const tries = Number(window.localStorage.getItem(RELOAD_TRIES_KEY) ?? 0);
      if (tries < MAX_RELOAD_TRIES) {
        window.localStorage.setItem(RELOAD_TRIES_KEY, String(tries + 1));
        window.location.reload();
      } else {
        window.localStorage.removeItem(RELOAD_TRIES_KEY);
        store.dispatch({ type: signOutAction.type });
      }
    },
  };

  configureApiClient(config);
  // Same auth and error handling as AP — its own URL prefix, and its own
  // query-string dialect (@/services/api/instances).
  eventsApi.configure({ ...config, baseURL: `${CONFIGURABLE_API_ENDPOINT || ''}/events/v1/` });
  // Same auth and error handling as AP — only the URL prefix differs.
  bankFeedApi.configure({ ...config, baseURL: `${CONFIGURABLE_API_ENDPOINT || ''}/bankfeed/v1/` });
}
